import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { get } from 'lodash';

import Header from 'components/Header';
import Heading from 'components/Heading';
import Footer from 'components/Footer';
import FormGroup from 'components/Form/FormGroup';
import ProgressBar from 'components/ProgressBar';
import { Grid, Column } from 'components/Layout';
import { Button } from 'components/Button';

import { formatCurrency } from 'utils/formatCurrency';

import {
  nextAction,
  checkPreviousAction,
} from 'actions/workflow';

import CircleContact from 'assets/icons/circle-contact.svg';
import style from './style.scss';

const OfferSuggestion = (props) => {
  const workflow = useSelector((state) => state.workflow);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isBankVerified, setIsBankVerified] = useState(false);
  const applicationId = get(workflow, 'attributes.entityId');

  useEffect(() => {
    const key = searchParams.get('key');

    if (searchParams.get('isBankVerified')) {
      setIsBankVerified(true);
    }

    if (key) {
      dispatch(checkPreviousAction({
        data: {},
        url: `/workflows/fsl/${key}/checkin`,
        success: (response) => {
          const routeUrl = response.data && response.data.url;
          navigate(routeUrl);
        },
        fail: () => {
          navigate('/fsl/application', { replace: true });
        },
      }));
    } else {
      navigate('/fsl/application', { replace: true });
    }
  }, []);

  const handleClickContinue = (e) => {
    e.preventDefault();
    const key = searchParams.get('key');

    if (key) {
      setIsLoading(true);
      dispatch(nextAction({
        data: {
          isConfirmed: true,
        },
        url: `/workflows/fsl/${key}/next`,
        success: (response) => {
          const routeUrl = response.data && response.data.url;
          navigate(routeUrl);
        },
        fail: (error) => {
          console.log(error);
        },
      }));
    }
  };

  return (
    <Fragment>
      <Header />
      <section className="grid-container fluid borrowers-apply">
        <div className={cn('grid-container fluid', isBankVerified && style['borrowers-message'])}>
          <Grid className="grid-margin-x max-limited">
            {
              <Fragment>
                {
                  isBankVerified ?
                    <Fragment>
                      <div className="cell small-12 large-8 large-offset-2">
                        {applicationId ? (
                          <div className={style.left}>
                            <p className="mb-0 p-large"><b>Application ID:</b> {applicationId}</p>
                          </div>
                        ) : null}
                        <img src={CircleContact} alt="circle contact" />
                        <h2>Thank You!</h2>
                        <p className="p-xlarge">That&apos;s all we need for now. We will reach out to you soon once we review and confirm your application.</p>
                      </div>
                    </Fragment>
                  :
                    <Fragment>
                      <Heading
                        heading="Review Your Loan Offer"
                        subHeading="Congratulations! You've been pre-approved for a loan."
                        isCardVisible={false}
                        className="large-offset-2 large-8"
                      />
                      <Column className="cell small-12 large-8 large-offset-2">
                        <ProgressBar step={2} totalStep={3} />
                        <FormGroup>
                          <Grid>
                            <div
                              className={cn('card loan-card', style.loanCard, style.selected)}
                            >
                              <Grid>
                                <Column className={cn('small-12 medium-4 highlight', style.cell, style.highlight)}>
                                  <h5>Estimated<br className="show-for-large" /> Monthly Payment</h5>
                                  <h3>${formatCurrency(get(workflow, 'data.data.paymentAmount'), 2) || ' -'}</h3>
                                </Column>
                                <Column className={cn('small-12 medium-8', style.cell, style.detail)}>
                                  <Grid>
                                    <Column className="small-6">
                                      <h5>Amount Financed</h5>
                                    </Column>
                                    <Column className="small-6">
                                      <h5><em>${formatCurrency(get(workflow, 'data.data.payoutAmount'), 2) || ' -'}</em></h5>
                                    </Column>
                                  </Grid>
                                  <Grid>
                                    <Column className="small-6">
                                      <h5>Loan Term</h5>
                                    </Column>
                                    <Column className="small-6">
                                      <h5><em>{get(workflow, 'data.data.term')} Months</em></h5>
                                    </Column>
                                  </Grid>
                                  <Grid>
                                    <Column className="small-6">
                                      <h5>Interest Rate</h5>
                                    </Column>
                                    <Column className="small-6">
                                      <h5><em>{formatCurrency(get(workflow, 'data.data.rate'), 2)}%</em></h5>
                                    </Column>
                                  </Grid>
                                  <Grid>
                                    <Column className="small-6">
                                      <h5>APR</h5>
                                    </Column>
                                    <Column className="small-6">
                                      <h5><em>{formatCurrency(get(workflow, 'data.data.apr'), 4)}%</em></h5>
                                    </Column>
                                  </Grid>
                                  <Grid>
                                    <Column className="small-6">
                                      <h5>Origination Fee*</h5>
                                    </Column>
                                    <Column className="small-6">
                                      <h5><em>{formatCurrency(get(workflow, 'data.data.originationPercentage') * 100, 0)}%</em></h5>
                                    </Column>
                                  </Grid>
                                </Column>
                              </Grid>
                            </div>
                            <Column className="small-12 medium-12">
                              <Button
                                className={cn('button large green w-100', style.button, isLoading ? '' : 'arrow')}
                                onClick={handleClickContinue}
                                isLoading={isLoading}
                              >
                                Next: Verify Bank Account
                              </Button>
                            </Column>
                            <Column className="small-12 medium-12">
                              <p className={style.description}>
                                * The Origination Fee may be up to 8% of the Amount Financed, is non-refundable, and is considered earned upon the funding of your loan. You can calculate the dollar amount of the Origination Fee for the offer listed above by multiplying the Amount Financed figure for the offer by .08 if the Origination Fee is 8%, by .06 if it is 6%, etc. For example, if the Origination Fee is 8% and if the Amount Financed is $1,000 the corresponding Origination Fee would be $80 ($1000 x .08 = $80). The total Principal Amount of your loan is the sum of the Amount Financed and the Origination Fee (i.e. if the Origination Fee is 8% and if the Amount Financed is $1,000 the Principal Amount of the loan would be $1080). Please review your Loan Agreement for additional terms and conditions.
                              </p>
                            </Column>
                          </Grid>
                        </FormGroup>
                      </Column>
                    </Fragment>
                }
              </Fragment>
            }
          </Grid>
        </div>
      </section>
      <Footer />
    </Fragment>
  );
};

export default OfferSuggestion;
